import AMap from 'AMap';
import { queryLocal } from './crossNative';
import { tryFn } from '@/utils/index';
import { isInApp } from '@/utils/is';
import { Toast } from 'vant';
/**
 * @typedef Coordinate 坐标
 * @property {number} lng 经度
 * @property {number} lat 纬度
 */

/**
 * @typedef AddressComponent 分段地址信息
 * @property {string} adcode
 * @property {string} building 建筑名
 * @property {string} buildingType 建筑类型
 * @property {string} city 城市名
 * @property {string} cityCode 城市编码
 * @property {string} country 国家名
 * @property {string} district 行政区名
 * @property {string} province 省名
 * @property {string} street 街道名称
 * @property {string} streetNumber 门牌号
 */

/**
 * @typedef Regeocode 高德逆解析出来的信息
 * @property {string} formattedAddress 详细地址字符串
 * @property {AddressComponent} addressComponent 分段地址信息
 */

/**
 * 通过定位权限获取当前位置坐标
 * @return {Promise<Coordinate>} 坐标
 */
const getHtml5Location = function() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition((position) => {
      resolve({
        lng: position.coords.longitude,
        lat: position.coords.latitude,
      });
    }, (err) => {
      reject(err);
    });
  });
};

/**
 * 通过高德API获取当前位置坐标
 * @return {Promise<Coordinate>} 坐标
 */
const aMapLocation = function() {
  return new Promise((resolve, reject) => {
    let geolocation = null;
    AMap.plugin('AMap.Geolocation', () => {
      geolocation = new AMap.Geolocation({
        enableHighAccuracy: true, // 是否使用高精度定位，默认:true
        timeout: 10000, // 超过10秒后停止定位，默认：无穷大
        buttonPosition: 'RB',
      });
      geolocation.getCurrentPosition((status, result) => { // 获取用户当前的精确位置
        console.log(status);
        if (status === 'complete') {
          resolve({
            lng: result.position.lng,
            lat: result.position.lat,
          });
        }
        else {
          reject();
        }
      });
    });
  });
};

/**
 * 通过高德API获取当前城市中心点坐标
 * @return {Promise<Coordinate>} 坐标
 */
const getCityCenterLocation = function() {
  return new Promise((resolve, reject) => {
    AMap.plugin('AMap.Geolocation', () => {
      const geolocation = new AMap.Geolocation({
        enableHighAccuracy: true, // 是否使用高精度定位，默认：true
        timeout: 10000, // 设置定位超时时间，默认：无穷大
        GeoLocationFirst: false, // 默认为false，设置为true的时候可以调整PC端为优先使用浏览器定位，失败后使用IP定位
      });
      // 精确定位失败时给当前定位城市的中心点的坐标
      geolocation.getCityInfo((status, result) => {
        console.log('定位城市的中心点', result);
        if (status === 'complete') {
          resolve({
            lng: result.center[0],
            lat: result.center[1],
          });
        }
        else {
          reject();
        }
      });
    });
  });
};

// 调用getLocationByApp后注册的resolve和reject对象数组
const appLocationHandlers = [];
// 向window注册方法供APP调用赋值
window.setCurrentLocation = function(lat, lng, status) {
  console.log('通过APP端设置经纬度', { lat, lng, status });
  if (!appLocationHandlers.length) return;
  const success = status && status !== '0' && parseInt(lat) && parseInt(lng);
  // 逐个resolve掉promise
  while (appLocationHandlers.length) {
    const { resolve, reject } = appLocationHandlers.pop();
    success ? resolve({ lat, lng }) : reject();
  }
};
/**
 * 获取位置信息,App注入到Window上
 * @return {Promise<Coordinate>} 坐标
 */
export const getLocationByApp = async function() {
  console.log('尝试从APP获取定位信息');
  return new Promise((resolve, reject) => {
    appLocationHandlers.push({ resolve, reject });
    // 向APP发起位置请求,同一时间只会请求一次，但是两个resolve都会调
    appLocationHandlers.length === 1 && queryLocal();
  });
};

/**
 * 综合获取当前位置坐标，逐步降级
 * @return {Promise<Coordinate>} 坐标
 */
export async function getCurrentLocation() {
  if (isInApp()) {
    try {
      return await tryFn(getLocationByApp, 5, 1000);
    }
    catch (e) {
      console.log('尝试从APP获取定位信息失败，开始尝试通过高德地图获取');
    }
  }
  try {
    return await aMapLocation();
  }
  catch (e) {
    console.log('尝试从高德地图获取定位信息失败，开始尝试使用浏览器原生定位');
  }
  // try {
  //   return await getHtml5Location();
  // }
  // catch (e) {
  //   console.log('尝试获取浏览器原生定位失败，开始使用当前定位城市中心点兜底');
  // }
  return getCityCenterLocation();
}

/**
 * 获取当前定位点的省市区等信息
 * @return {Promise<Regeocode>}
 */
export async function getCurrentAddress() {
  const { lng, lat } = await getCurrentLocation();
  return getAddressByCords([lng, lat]);
}

/**
 * 通过坐标信息逆地址解析出当前省市区等信息
 * @param {number[]} lnglat 经纬度数组[longitude,latitude]
 * @return {Promise<Regeocode>}
 */
export function getAddressByCords(lnglat) {
  return new Promise((resolve, reject) => {
    const geocoder = new AMap.Geocoder({});
    geocoder.getAddress(lnglat, (status, result) => {
      if (status === 'complete') {
        resolve(result.regeocode);
      }
      else {
        console.log('地理位置解析失败');
        Toast('地理位置解析失败');
      }
    });
  });
}

/**
 * 计算两地经纬度距离
 * lng1, lat1 第1个经纬度
 * lng2, lat2 第2个经纬度
 * @param {*} lng1 number类型
 * @param {*} lat1
 * @param {*} lng2
 * @param {*} lat2
*/
export const calculateDistance = (lng1, lat1, lng2, lat2) => {
  const radLat1 = (lat1 * Math.PI) / 180.0;
  const radLat2 = (lat2 * Math.PI) / 180.0;
  const a = radLat1 - radLat2;
  const b = (lng1 * Math.PI) / 180.0 - (lng2 * Math.PI) / 180.0;
  let s = 2 *
    Math.asin(
      Math.sqrt(
        Math.pow(Math.sin(a / 2), 2) +
          Math.cos(radLat1) * Math.cos(radLat2) * Math.pow(Math.sin(b / 2), 2),
      ),
    );
  s = s * 6378.137;
  s = Math.round(s * 10000) / 10000;
  s = s * 1000;
  if (isNaN(s)) {
    return 0 + 'm';
  }
  if (s > 100000) {
    // 大于100Km时
    s = Math.floor((s / 1000) * 100) / 100;
    return s.toFixed() + 'km';
  }
  if (s > 1000 && s < 100000) {
    // 大于1000米时
    s = Math.floor((s / 1000) * 100) / 100;
    return s.toFixed(1) + 'km';
  }
  // 小于1000米直接返回
  return s.toFixed() + 'm';
};
